// Authentication service handling OpenID Connect (OIDC) integration with PKCE flow,
// including automatic silent renew, axios bearer-token sync, and SSO redirect handling.
// Exports a provider and a custom hook used by other common code. Application feature
// code should not need to use this provider directly as authentication is handled in
// the background, allowing the application to assume the current user is authenticated
// (but not necessarily authorized, which the application checks with the Users service).
//
// Uses react-oidc-context as the React integration layer and oidc-client-ts as the
// underlying OIDC implementation. The react-oidc-context package provides React context
// and hooks around oidc-client-ts, while the oidc-client-ts package handles protocol
// details such as PKCE, callback processing, token storage, and silent renew.
// https://github.com/authts/react-oidc-context#documentation
// https://authts.github.io/oidc-client-ts
//
// OIDC configuration for the SSO provider and environment the application is running
// in comes from RUNTIME_CONFIG settings oidc.issuerUri, oidc.clientId, and oidc.scope.
// Missing runtime settings fail fast with a configuration error. The Vite base URL
// environment variable controls the redirect URI base path.
//
// The SSO provider redirects back with temporary callback parameters such as code,
// state, session_state, iss, error, error_description, or error_uri.
// After react-oidc-context processes those parameters, onSigninCallback restores the
// saved application URL. If none was saved, it removes the callback parameters from
// the current URL so they are not processed again on reload or silent renew.
//
// Tokens are refreshed by oidc-client-ts automatic silent renew. This provider syncs
// the current access token to axios and removes it when no user is available. If renewal
// fails, this provider tries normal silent recovery, then fresh silent authorization
// through the SSO session, before restarting SSO.
//
// OIDC request state and authenticated user state are stored in sessionStorage. A
// hard reload in the same tab can reuse a valid stored OIDC user. Closing the tab
// normally clears this state, though browser session restoration can preserve it.
// There is no session-expired page or explicit logout flow, as these are not
// currently needed by applications.
//
// Startup/login flow:
// - Trigger: react-oidc-context finishes initialization/callback processing and this
//   provider sees no authenticated user.
// - If the URL has OIDC callback params, react-oidc-context processes them and
//   onSigninCallback restores the saved path, query string, and hash, or cleans the
//   callback URL if no return path is available.
// - If a valid user exists in sessionStorage or from the callback, axios receives the
//   access token and the application renders.
// - If no authenticated user exists after initialization/callback handling, this
//   provider saves the current URL in signinRedirect() state and leaves the app for SSO.
//
// Token recovery flow:
// - Trigger: oidc-client-ts emits access-token-expired or silent-renew-error.
// - This provider tries normal signinSilent() recovery, then one forced iframe
//   authorization if needed, without leaving the app.
// - If silent recovery returns a valid user, axios receives the refreshed token and
//   the application continues.
// - If both attempts fail or return no valid user, this provider removes local
//   OIDC user state. That unauthenticated state triggers the startup/login flow,
//   which calls signinRedirect() to restart SSO.

import axios from "axios";
import { UserManager, WebStorageStateStore } from "oidc-client-ts";
import { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import {
  AuthProvider as OidcAuthProvider,
  hasAuthParams,
  useAuth as useOidcAuth,
} from "react-oidc-context";

// Build the application callback URL from the current origin and configured Vite base path
const getOidcRedirectUri = () =>
  new URL(import.meta.env.BASE_URL, window.location.origin).toString();

// Create a per-tab OIDC storage adapter for request state and authenticated user state
const createSessionStorageStore = () =>
  new WebStorageStateStore({ store: window.sessionStorage });

// Share an ongoing silent renewal between the library's automatic renewal and
// our expiration recovery, so they cannot exchange the same refresh token twice
// concurrently. Apply to both providers; clear after success or failure so the
// next attempt, including forced iframe recovery, can run normally.
const shareSilentRenewal = (userManager) => {
  const signinSilent = userManager.signinSilent.bind(userManager);
  const pending = { promise: null };

  userManager.signinSilent = (args) => {
    if (!pending.promise) {
      pending.promise = Promise.resolve()
        .then(() => signinSilent(args))
        .finally(() => {
          pending.promise = null;
        });
    }
    return pending.promise;
  };
};

// TODO: Remove the following Keycloak compatibility patch once the switch to Entra ID is complete
// Dynamically patches the oidc-client-ts package's ID-token validation when the
// application is configured to use Keycloak. Entra ID keeps the original validation.
// Our Keycloak deployment can return a different authentication time (auth_time)
// during token refresh after another tab authenticates. The library rejects that
// response before saving the new tokens, causing our recovery flow to restart SSO
// and lose unsaved form changes. Ignoring the error would leave expired tokens in use.
// Allow auth_time to change so the library can finish refreshing tokens normally,
// while retaining its subject, nonce, and authorized-party checks. This replaces a
// private method on the authentication manager created for this app tab; it does
// not modify the installed package files or other authentication managers.
const patchKeycloakAuthTimeValidation = (userManager) => {
  // Recognize Keycloak's realm issuer path, including deployments under /auth.
  const issuerPath = new URL(userManager.settings.authority).pathname;
  if (!/\/realms\/[^/]+\/?$/.test(issuerPath)) return;

  const validator = userManager._client._validator;
  const validate = validator._validateIdTokenAttributes.bind(validator);

  validator._validateIdTokenAttributes = (response, existingToken, nonce) => {
    // Omit the previous token here to skip the library's cross-token comparisons.
    // The original method still checks the incoming subject and expected nonce.
    // Below, restore the subject and authorized-party comparisons, deliberately
    // leaving out the auth_time equality check.
    validate(response, undefined, nonce);
    if (!existingToken) return;

    // Use the library to decode the previous token as well.
    const previous = { id_token: existingToken };
    validate(previous);

    const incoming = response.profile;
    const existing = previous.profile;

    if (incoming.sub !== existing.sub) {
      throw new Error("ID token subject changed during refresh.");
    }
    if (incoming.azp && incoming.azp !== existing.azp) {
      throw new Error("ID token authorized party changed during refresh.");
    }
    if (!incoming.azp && existing.azp) {
      throw new Error("ID token authorized party is missing after refresh.");
    }
  };
};

// Restore the requested URL after sign-in, falling back to cleaning the callback URL.
// react-oidc-context passes the signed-in user; error cleanup calls this without a user.
const restoreAppUrl = (user) => {
  const url = new URL(window.location.href);

  url.searchParams.delete("code");
  url.searchParams.delete("state");
  url.searchParams.delete("session_state");
  url.searchParams.delete("iss");
  url.searchParams.delete("error");
  url.searchParams.delete("error_description");
  url.searchParams.delete("error_uri");

  // OIDC returns the custom sign-in state on user.state. Restoring that URL also
  // removes the callback parameters without reloading the application.
  window.history.replaceState(
    {},
    document.title,
    user?.state?.returnTo ?? `${url.pathname}${url.search}${url.hash}`,
  );
};

// Create the OIDC configuration object from runtime settings and typical defaults
const createOidcConfig = () => {
  const oidc = window.RUNTIME_CONFIG?.oidc;

  if (!oidc?.issuerUri || !oidc?.clientId || !oidc?.scope) {
    throw new Error(
      "OIDC configuration is missing or incomplete in window.RUNTIME_CONFIG.",
    );
  }

  return {
    authority: oidc.issuerUri,
    client_id: oidc.clientId,
    redirect_uri: getOidcRedirectUri(),
    response_type: "code",
    scope: oidc.scope,
    refreshTokenAllowedScope: oidc.scope,
    automaticSilentRenew: true,
    loadUserInfo: false,
    monitorSession: false,
    revokeTokensOnSignout: false,
    stateStore: createSessionStorageStore(),
    userStore: createSessionStorageStore(),
    onSigninCallback: restoreAppUrl,
  };
};

// Attach the current access token to axios requests
const setAuthorizationHeader = (accessToken) => {
  axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
};

// Clear the axios bearer token when auth state no longer has an access token
const removeAuthorizationHeader = () => {
  delete axios.defaults.headers.common.Authorization;
};

// Limit logged error details to text fields, excluding nested authentication data.
const getErrorLogDetails = (error) => ({
  name: typeof error?.name === "string" ? error.name : "Error",
  message: typeof error?.message === "string" ? error.message : "Unknown error",
});

// Keep axios and SSO redirect behavior synchronized with the OIDC auth lifecycle
const AuthLifecycle = ({ children }) => {
  const {
    activeNavigator,
    error,
    events,
    isAuthenticated,
    isLoading,
    removeUser,
    signinRedirect,
    signinSilent,
    user,
  } = useOidcAuth();
  const redirectInProgressRef = useRef(false);
  const recoveryInProgressRef = useRef(false);

  // Set the axios token before child useEffect API calls run, including the initial users/me request
  useLayoutEffect(() => {
    if (user?.access_token) {
      setAuthorizationHeader(user.access_token);
      return;
    }

    removeAuthorizationHeader();
  }, [user?.access_token]);

  // Track redirect state and send unauthenticated users to SSO after initialization
  useEffect(() => {
    // A valid session means any previous redirect attempt is complete
    if (isAuthenticated) {
      redirectInProgressRef.current = false;
      return;
    }

    const isProcessingAuthCallback = hasAuthParams() && !error;

    // Wait until auth startup/callback/navigation/recovery work has settled
    if (
      isProcessingAuthCallback ||
      isLoading ||
      activeNavigator ||
      recoveryInProgressRef.current ||
      redirectInProgressRef.current
    ) {
      return;
    }

    redirectInProgressRef.current = true;

    // Clear failed callback params before redirecting so they are not replayed
    if (error) {
      console.warn(
        "OIDC authentication failed; redirecting to SSO.",
        getErrorLogDetails(error),
      );
      restoreAppUrl();
    } else {
      console.debug("OIDC authentication is required; redirecting to SSO.");
    }

    // SSO returns to the fixed app base URL. Save the requested location in OIDC
    // state so the callback can restore the path, query string, and hash afterward.
    void signinRedirect({
      state: {
        returnTo: `${window.location.pathname}${window.location.search}${window.location.hash}`,
      },
    })
      .then((redirectResult) => {
        // react-oidc-context returns null when the redirect call fails before navigation
        if (redirectResult === null) {
          redirectInProgressRef.current = false;
        }
      })
      .catch((redirectError) => {
        redirectInProgressRef.current = false;
        console.error(
          "OIDC authentication redirect failed.",
          getErrorLogDetails(redirectError),
        );
      });
  }, [activeNavigator, error, isAuthenticated, isLoading, signinRedirect]);

  // On token-renewal failure, exhaust silent recovery before restarting SSO
  useEffect(() => {
    // Treat both a rejected call and a null/invalid user as a failed attempt.
    // react-oidc-context can return null on failure instead of throwing.
    const trySilentRecovery = async (args) => {
      try {
        const recoveredUser = await signinSilent(args);
        if (recoveredUser?.access_token && !recoveredUser.expired) {
          return true;
        }
        console.warn(
          "OIDC silent recovery attempt did not return a valid user.",
        );
      } catch (recoveryError) {
        console.warn(
          "OIDC silent recovery attempt failed.",
          getErrorLogDetails(recoveryError),
        );
      }
      return false;
    };

    // Keep recovery bounded and allow only one recovery sequence at a time
    const recoverAuthentication = async (message, recoveryReason) => {
      if (recoveryInProgressRef.current || redirectInProgressRef.current) {
        return;
      }

      recoveryInProgressRef.current = true;
      if (recoveryReason !== undefined) {
        console.warn(message, getErrorLogDetails(recoveryReason));
      } else {
        console.debug(message);
      }

      // First, retry normal silent renewal for either provider, using the stored
      // refresh token when available.
      if (await trySilentRecovery()) {
        recoveryInProgressRef.current = false;
        return;
      }

      // If that fails, obtain fresh authorization through the SSO session in an
      // iframe. The library still verifies that the user has not changed.
      if (await trySilentRecovery({ forceIframeAuth: true })) {
        recoveryInProgressRef.current = false;
        return;
      }

      // Only clear local auth state after both attempts fail so normal SSO can restart
      console.warn("OIDC silent recovery exhausted; restarting SSO.");
      try {
        await removeUser();
      } catch (removeUserError) {
        console.error(
          "OIDC user cleanup failed before SSO.",
          getErrorLogDetails(removeUserError),
        );
      } finally {
        recoveryInProgressRef.current = false;
      }
    };

    // Register OIDC event listeners that must be removed when React cleans up this effect
    const removeEventListeners = [
      events.addAccessTokenExpired(() => {
        void recoverAuthentication(
          "OIDC access token expired; attempting silent recovery.",
        );
      }),
      events.addSilentRenewError((renewError) => {
        void recoverAuthentication(
          "OIDC token refresh failed; attempting silent recovery.",
          renewError,
        );
      }),
    ];

    // Remove registered OIDC event listeners before this effect runs again or unmounts
    return () => {
      removeEventListeners.forEach((removeEventListener) => {
        removeEventListener();
      });
    };
  }, [events, removeUser, signinSilent]);

  return children;
};

// Context provider for the OIDC state and authentication lifecycle
export const AuthProvider = ({ children }) => {
  const oidcConfig = useMemo(createOidcConfig, []);
  const userManager = useMemo(() => {
    const manager = new UserManager(oidcConfig);
    shareSilentRenewal(manager);
    // TODO: Remove the following line once the switch to Entra ID is complete
    patchKeycloakAuthTimeValidation(manager);
    return manager;
  }, [oidcConfig]);

  return (
    <OidcAuthProvider {...oidcConfig} userManager={userManager}>
      <AuthLifecycle>{children}</AuthLifecycle>
    </OidcAuthProvider>
  );
};

// Custom hook to access the context
export const useAuth = () => {
  return useOidcAuth();
};
